import { Clock, Info } from 'lucide-react';
import {
  getWeeklyTheoreticalHours,
  isEvenWeek,
  resolveWeeklyHours,
} from '../../utils/overtimeUtils';
import { dateKey, generateWeekDays, getWeekNumber, MONTHS_FR } from '../../utils/dateUtils';
import type { GridRow, DailyIndemnity } from './types';

interface WeekNavigatorProps {
  currentWeekStart: Date;
  weekMonths: { year: number; month: number; days: number; firstDay: Date }[];
  isStraddleWeek: boolean;
  activeEntryMonth: { year: number; month: number } | undefined;
  showMonthTransition: boolean;
  changeWeek: (dir: number) => void;
  setEntryMonth: (m: { year: number; month: number }) => void;
  setShowMonthTransition: (v: boolean) => void;
  holidays: string[];
  trainingDays: string[];
  isBE: boolean;
  isBE35h: boolean;
  customWeeklyHours: number | null;
  overrideWeeklyHours: number | null;
  rows: GridRow[];
  indemnities: Record<string, DailyIndemnity>;
  isDayBlocked: (day: Date) => boolean;
}

export function WeekNavigator({
  currentWeekStart,
  weekMonths,
  isStraddleWeek,
  activeEntryMonth,
  showMonthTransition,
  changeWeek,
  setEntryMonth,
  setShowMonthTransition,
  holidays,
  trainingDays,
  isBE,
  isBE35h,
  customWeeklyHours,
  overrideWeeklyHours,
  rows,
  indemnities,
  isDayBlocked,
}: WeekNavigatorProps) {
  return (
    <>
      {/* NAVIGATEUR SEMAINE AVEC STEPPER POUR SEMAINES À CHEVAL */}
      <div style={{ background: 'white', padding: '20px', borderRadius: '20px', border: '1px solid #e2e8f0', marginBottom: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={() => changeWeek(-1)} style={{ padding: '16px 24px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '1.5rem', cursor: 'pointer', color:'#64748b' }}>‹</button>
          <div style={{ textAlign: 'center', position: 'relative' }}>
            <div style={{ fontSize: '0.9rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight:'600', marginBottom:'6px' }}>Semaine {getWeekNumber(currentWeekStart)}</div>

            {/* Titre du mois avec animation de transition */}
            <div style={{
              fontSize: '1.5rem',
              fontWeight: '800',
              color:'#1e293b',
              transition: 'opacity 0.15s ease-in-out, transform 0.15s ease-in-out',
              opacity: showMonthTransition ? 0 : 1,
              transform: showMonthTransition ? 'translateX(-20px)' : 'translateX(0)'
            }}>
              {activeEntryMonth ? `${MONTHS_FR[activeEntryMonth.month]} ${activeEntryMonth.year}` : currentWeekStart.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </div>

            {/* Stepper pour semaines à cheval */}
            {isStraddleWeek && (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                marginTop: '10px'
              }}>
                {weekMonths.map((m) => {
                  const isActive = activeEntryMonth &&
                                   m.year === activeEntryMonth.year &&
                                   m.month === activeEntryMonth.month;
                  return (
                    <button
                      key={`${m.year}-${m.month}`}
                      onClick={() => {
                        setShowMonthTransition(true);
                        setTimeout(() => {
                          setEntryMonth({ year: m.year, month: m.month });
                          setShowMonthTransition(false);
                        }, 150);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        border: isActive ? '2px solid #2563eb' : '1px solid #e2e8f0',
                        background: isActive ? '#eff6ff' : 'white',
                        color: isActive ? '#2563eb' : '#64748b',
                        fontWeight: isActive ? '700' : '500',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <span style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: isActive ? '#2563eb' : '#cbd5e1'
                      }}></span>
                      {MONTHS_FR[m.month].substring(0, 4)}.
                      <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>({m.days}j)</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button onClick={() => changeWeek(1)} style={{ padding: '16px 24px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '1.5rem', cursor: 'pointer', color:'#64748b' }}>›</button>
        </div>

        {/* Indication semaine à cheval */}
        {isStraddleWeek && (
          <div style={{
            marginTop: '12px',
            padding: '10px 16px',
            background: '#fefce8',
            border: '1px solid #fef08a',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.85rem',
            color: '#854d0e'
          }}>
            <Info size={16} />
            <span>
              <strong>Semaine à cheval :</strong> Vous saisissez actuellement pour <strong>{activeEntryMonth ? MONTHS_FR[activeEntryMonth.month] : ''}</strong>.
              Utilisez les boutons ci-dessus pour basculer vers l'autre mois.
            </span>
          </div>
        )}
      </div>

      {/* INFORMATIONS SEMAINE PAIRE/IMPAIRE ET HEURES SUPPLÉMENTAIRES */}
      {(() => {
        const weekNum = getWeekNumber(currentWeekStart);
        const even = isEvenWeek(weekNum);
        // Override > Custom hours (intérimaires) > BE (35h fixe) > logique pair/impair
        const effectiveCustomHours = resolveWeeklyHours(overrideWeeklyHours, customWeeklyHours);
        const standardTheoreticalHours = effectiveCustomHours
          ? effectiveCustomHours
          : isBE35h ? 35 : getWeeklyTheoreticalHours(weekNum);

        // Calcul des heures saisies et détection GD
        let totalNormal = 0;
        let totalSaturday = 0;
        let totalSunday = 0;
        let gdDaysCount = 0; // Nombre de jours ouvrés en GD
        let holidaysInWeekCount = 0; // Nombre de jours fériés dans la semaine (Lun-Ven)

        const weekDaysLocal = generateWeekDays(currentWeekStart);
        weekDaysLocal.forEach(day => {
          const dKey = dateKey(day);
          const dayOfWeek = day.getDay();

          // Compter les jours fériés (Lundi-Vendredi uniquement)
          if (dayOfWeek >= 1 && dayOfWeek <= 5 && holidays.includes(dKey)) {
            holidaysInWeekCount++;
          }

          // Formation = 7h
          if (trainingDays.includes(dKey)) {
            totalNormal += 7;
            return;
          }

          // Jours fériés et week-end : ignorer pour le comptage GD
          if (isDayBlocked(day)) return;

          // Compter les jours ouvrés en GD (Lundi-Vendredi uniquement)
          if (indemnities[dKey]?.is_grand_deplacement && dayOfWeek >= 1 && dayOfWeek <= 5) {
            gdDaysCount++;
          }

          rows.forEach(row => {
            const h = row.hours[dKey] || 0;
            if (dayOfWeek === 0) totalSunday += h;       // Dimanche
            else if (dayOfWeek === 6) totalSaturday += h; // Samedi
            else totalNormal += h;                        // Lun-Ven
          });
        });

        const hoursLuSa = totalNormal + totalSaturday;
        const totalAll = hoursLuSa + totalSunday;

        // RÈGLE GD : Si au moins un jour est en GD, les heures théoriques = 35h FIXE
        // Si jour férié dans la semaine : retirer 7h par jour férié
        const hasGDThisWeek = gdDaysCount > 0;
        let theoreticalHours = standardTheoreticalHours;
        let gdTheoreticalInfo = '';

        if (hasGDThisWeek) {
          // GD = 35h fixe, réparti sur Lun-Jeu (4 jours × 8h45)
          // Si jour férié : retirer 7h par jour férié
          const baseGDHours = 35;
          const hoursDeductedForHolidays = holidaysInWeekCount * 7;
          theoreticalHours = baseGDHours - hoursDeductedForHolidays;

          if (holidaysInWeekCount > 0) {
            gdTheoreticalInfo = `GD: 35h - ${holidaysInWeekCount} férié(s) = ${theoreticalHours}h`;
          } else {
            gdTheoreticalInfo = `GD: 35h (Lun-Jeu)`;
          }
        }

        // Couleurs selon le type : GD (rouge), BE (violet), Custom (orange), Paire (bleu), Impaire (jaune)
        const getBannerStyle = () => {
          if (hasGDThisWeek) return { bg: '#fef2f2', border: '#fecaca', accent: '#dc2626', text: '#b91c1c', separator: '#fca5a5' };
          if (isBE) return { bg: '#f5f3ff', border: '#ddd6fe', accent: '#7c3aed', text: '#6d28d9', separator: '#c4b5fd' };
          if (effectiveCustomHours) return { bg: '#fff7ed', border: '#fed7aa', accent: '#ea580c', text: '#c2410c', separator: '#fdba74' };
          if (even) return { bg: '#eff6ff', border: '#bfdbfe', accent: '#2563eb', text: '#1e40af', separator: '#93c5fd' };
          return { bg: '#fefce8', border: '#fef08a', accent: '#ca8a04', text: '#854d0e', separator: '#fde047' };
        };
        const style = getBannerStyle();

        // Label selon le type
        const getWeekLabel = () => {
          if (hasGDThisWeek) return 'GRAND DÉPLACEMENT';
          if (effectiveCustomHours) {
            if (isBE) return `BUREAU D'ÉTUDE ${effectiveCustomHours}H`;
            return `CONTRAT ${effectiveCustomHours}H`;
          }
          if (isBE35h) return 'BUREAU D\'ÉTUDE';
          return even ? 'SEMAINE PAIRE' : 'SEMAINE IMPAIRE';
        };

        // Info bulle selon le type
        const getInfoText = () => {
          if (hasGDThisWeek) return gdTheoreticalInfo;
          if (effectiveCustomHours) return `Base ${effectiveCustomHours}h/semaine`;
          if (isBE35h) return '35h/semaine (Lun-Ven: 7h)';
          return even ? 'Lun-Jeu: 8h, Ven: 6h' : 'Lun-Jeu: 8h, Ven: repos';
        };

        return (
          <div style={{
            background: style.bg,
            border: `1px solid ${style.border}`,
            borderRadius: '16px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px'
          }}>
            {/* Type de semaine */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                background: style.accent,
                color: 'white',
                padding: '6px 12px',
                borderRadius: '8px',
                fontWeight: '700',
                fontSize: '0.85rem'
              }}>
                {getWeekLabel()}
              </div>
              <span style={{ color: style.text, fontWeight: '600' }}>
                {theoreticalHours}h théoriques
              </span>
            </div>

            {/* Séparateur */}
            <div style={{ width: '1px', height: '24px', background: style.separator }}></div>

            {/* Heures saisies */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} color={style.accent} />
              <span style={{ color: '#334155', fontWeight: '600' }}>
                Saisies : <strong style={{ fontSize: '1.1rem' }}>{totalAll.toFixed(2)}h</strong>
              </span>
            </div>

            {/* Info bulle */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '0.8rem' }}>
              <Info size={14} />
              <span>{getInfoText()}</span>
            </div>
          </div>
        );
      })()}
    </>
  );
}
